"use client"

// Careers global search: Cmd/Ctrl+K command palette over /api/career/search (deterministic, no
// LLM). Built from kit tokens + primitives (not the kit CommandPalette, which is sync-filter-only
// with no async/onQueryChange hook; extending the shared kit would also pull in its dist-build
// step). Mounts in the shared AppBar `search` slot (authed shell) and the public MarketingHeader
// slot, so one control serves both. Interaction mirrors GTM's GlobalSearchBar: debounced fetch,
// grouped results, arrow/enter/escape nav, mobile friendly. Results navigate to existing surfaces.
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { Search, Clock } from "lucide-react"
import { relevanceScore } from "@/lib/career/searchRelevance"

interface Opp { id: string; role_title: string; employer: string | null; location: string | null; term_season: string | null; term_year: number | null; career_track: string | null }
interface Company { name: string; company_id: string | null; open_count: number }
interface Role { track: string; label: string; role_slug: string | null }
interface Skill { name: string; category: string | null }
interface Results { query: string; opportunities: Opp[]; companies: Company[]; roles: Role[]; skills: Skill[] }
type Kind = "Internship" | "Employer" | "Role" | "Skill"
interface RankedItem { key: string; kind: Kind; title: string; subtitle: string; href: string; score: number; actionable: boolean }

// ONE relevance-ranked list across every result type - NOT a fixed "internships first" order. Each
// candidate is scored against the query by the shared relevanceScore, and the best textual match
// wins whatever its kind. Internships land on the feed (the internship listing) with that role open;
// an employer opens its company page (where Follow / its open roles live).
function rankResults(r: Results): RankedItem[] {
  const q = r.query
  const items: RankedItem[] = []
  for (const o of r.opportunities)
    items.push({
      key: `o:${o.id}`, kind: "Internship", title: o.role_title,
      subtitle: [o.employer, o.location, o.term_season && o.term_year ? `${o.term_season} ${o.term_year}` : null].filter(Boolean).join("  ·  "),
      href: `/feed?opp=${encodeURIComponent(o.id)}`,
      score: Math.max(relevanceScore(o.role_title, q), relevanceScore(o.employer, q)), actionable: true,
    })
  for (const c of r.companies)
    items.push({
      key: `c:${c.company_id || c.name}`, kind: "Employer", title: c.name,
      // 0 roles is normal (the graph search surfaces employers we hold no posting for). Say so and
      // point at the action: the company page has Follow, so a student can watch-list it.
      subtitle: c.open_count > 0 ? `${c.open_count} open role${c.open_count === 1 ? "" : "s"}` : "No open roles now · follow to track",
      href: c.company_id ? `/company/${c.company_id}` : `/feed?q=${encodeURIComponent(c.name)}`,
      score: relevanceScore(c.name, q), actionable: c.open_count > 0,
    })
  // A role search sends the student to their FEED filtered to that track - NOT the public
  // /careers/<slug> SEO page (that page is for organic/Google discovery, not an interactive search;
  // landing an in-app searcher there is the wrong destination). The feed reads ?tracks= on load.
  for (const rf of r.roles)
    items.push({ key: `r:${rf.track}`, kind: "Role", title: rf.label, subtitle: "Role family", href: `/feed?tracks=${encodeURIComponent(rf.track)}`, score: relevanceScore(rf.label, q), actionable: false })
  for (const s of r.skills)
    items.push({ key: `s:${s.name}`, kind: "Skill", title: s.name, subtitle: s.category || "Skill", href: `/feed?q=${encodeURIComponent(s.name)}`, score: relevanceScore(s.name, q), actionable: false })
  // Relevance-first. Tie-break only: an actionable row (an internship, or an employer with open
  // roles) edges out an equal-scoring inert one, then alphabetical for stability. Not a type
  // priority - a stronger textual match of any kind still leads.
  return items
    .filter((it) => it.score > 0)
    .sort((a, b) => b.score - a.score || (b.actionable ? 1 : 0) - (a.actionable ? 1 : 0) || a.title.localeCompare(b.title))
    .slice(0, 12)
}

const KBD: React.CSSProperties = { fontFamily: "var(--gtm-mono, ui-monospace)", fontSize: 10.5, padding: "1px 5px", borderRadius: 5, border: "1px solid var(--border-subtle)", color: "var(--text-muted)", background: "var(--surface-sunken, transparent)" }

// Recent searches: the last few queries the student actually COMMITTED (ran a feed search, or opened
// a result), most-recent-first. Shown when the palette opens empty, so a repeat search is one click.
// Per-viewer convenience only - never shared, never read back by the server - so localStorage is the
// right store; every access is guarded because it throws in private windows / storage-blocked modes.
const RECENTS_KEY = "careers:recent-searches"
const RECENTS_MAX = 7
function readRecents(): string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY)
    const v = raw ? JSON.parse(raw) : []
    return Array.isArray(v) ? v.filter((x) => typeof x === "string" && x.trim()).slice(0, RECENTS_MAX) : []
  } catch { return [] }
}
function writeRecents(list: string[]) {
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX))) } catch { /* private mode / storage disabled */ }
}

export function GlobalSearch() {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState("")
  const [results, setResults] = useState<Results | null>(null)
  const [loading, setLoading] = useState(false)
  const [active, setActive] = useState(0)
  // Whether the user has arrow-navigated to a specific result. Default Enter (no navigation) runs a
  // full feed search (/feed?q=...) rather than opening the top result - that's what a title-searcher
  // expects. Arrowing to a result and pressing Enter opens THAT result.
  const [nav, setNav] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const seq = useRef(0)
  // Loaded after mount (localStorage is client-only, so initial render stays [] and hydration matches).
  const [recent, setRecent] = useState<string[]>([])
  useEffect(() => { setRecent(readRecents()) }, [])
  const pushRecent = useCallback((term: string) => {
    const t = term.trim()
    if (t.length < 2) return
    setRecent((prev) => {
      const next = [t, ...prev.filter((x) => x.toLowerCase() !== t.toLowerCase())].slice(0, RECENTS_MAX)
      writeRecents(next)
      return next
    })
  }, [])
  const clearRecents = useCallback(() => { setRecent([]); writeRecents([]) }, [])
  // Click / Enter a recent term -> refill the box and re-run its search (results appear); the student
  // can then open a result or press Enter to land on the filtered feed, exactly as a fresh search.
  const applyRecent = useCallback((term: string) => { setQ(term); setNav(false); setActive(0); inputRef.current?.focus() }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((v) => !v) }
      else if (e.key === "Escape") setOpen(false)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  useEffect(() => { if (open) { const t = setTimeout(() => inputRef.current?.focus(), 30); return () => clearTimeout(t) } }, [open])

  useEffect(() => {
    if (!open) return
    const query = q.trim()
    if (query.length < 2) { setResults(null); setLoading(false); return }
    setLoading(true)
    const my = ++seq.current
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`/api/career/search?q=${encodeURIComponent(query)}`)
        const d = await r.json()
        if (my !== seq.current) return
        setResults(d?.ok ? (d as Results) : null)
        setActive(0)
      } catch { if (my === seq.current) setResults(null) } finally { if (my === seq.current) setLoading(false) }
    }, 200)
    return () => clearTimeout(t)
  }, [q, open])

  const items = useMemo(() => (results ? rankResults(results) : []), [results])
  const run = useCallback((it: RankedItem) => {
    pushRecent(q) // opening a result counts as a committed search - remember what led here
    setOpen(false); setQ(""); setResults(null)
    router.push(it.href)
    // If this is an internship, also signal the feed directly so its detail drawer opens even when
    // we are ALREADY on /feed - a same-route ?opp= URL change does not remount the feed to re-read
    // the URL. Landing on /feed from another page is covered by the feed's mount-time ?opp= read.
    if (it.kind === "Internship" && it.key.startsWith("o:")) {
      try { window.dispatchEvent(new CustomEvent("careers:open-opp", { detail: it.key.slice(2) })) } catch { /* no window */ }
    }
  }, [router, q, pushRecent])

  const onKeyDown = (e: React.KeyboardEvent) => {
    // When the box is empty, the visible list is the recent searches; otherwise it's the results.
    const showingRecents = q.trim().length < 2 && recent.length > 0
    const list = showingRecents ? recent : items
    if (e.key === "ArrowDown") { e.preventDefault(); setNav(true); setActive((a) => Math.min(a + 1, list.length - 1)) }
    else if (e.key === "ArrowUp") { e.preventDefault(); setNav(true); setActive((a) => Math.max(a - 1, 0)) }
    else if (e.key === "Enter") {
      e.preventDefault()
      // Empty box + recents shown -> Enter re-runs the highlighted recent search.
      if (showingRecents) { const term = recent[active]; if (term) applyRecent(term); return }
      // Arrowed to a specific result -> open it. Otherwise (just typed + Enter) -> search the feed
      // for the raw text, so the student lands on their filtered feed, never a static SEO page.
      if (nav && items[active]) run(items[active])
      else if (q.trim().length >= 2) { const query = q.trim(); pushRecent(query); setOpen(false); setQ(""); setResults(null); setNav(false); router.push(`/feed?q=${encodeURIComponent(query)}`) }
    }
  }

  const qlen = q.trim().length
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 34, padding: "0 10px", borderRadius: 9, border: "1px solid var(--border-subtle)", background: "var(--surface-sunken, transparent)", color: "var(--text-muted)", cursor: "pointer", fontSize: 13, maxWidth: 260, minWidth: 0 }}
      >
        <Search size={14} />
        <span style={{ opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Search</span>
        <kbd style={{ ...KBD, marginLeft: "auto" }}>⌘K</kbd>
      </button>

      {open && (
        <div role="dialog" aria-modal="true" onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(6,12,24,0.55)", backdropFilter: "blur(2px)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "12vh 16px 16px" }}>
          <div className="theme-max smx" onClick={(e) => e.stopPropagation()} onKeyDown={onKeyDown} style={{ width: "100%", maxWidth: 620, background: "var(--surface)", border: "1px solid var(--border-subtle)", borderRadius: 14, boxShadow: "0 24px 60px rgba(0,0,0,0.45)", overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "70vh" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: "1px solid var(--border-subtle)" }}>
              <Search size={16} style={{ opacity: 0.6, flexShrink: 0 }} />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => { setQ(e.target.value); setNav(false); setActive(0) }}
                placeholder="Search internships, employers, roles, skills…"
                style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "var(--text-primary)", fontSize: 15 }}
              />
              {loading && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>searching…</span>}
            </div>

            <div style={{ overflowY: "auto", padding: "6px 6px 8px" }}>
              {qlen < 2 ? (
                recent.length > 0 ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px 4px" }}>
                      <span style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)" }}>Recent searches</span>
                      <button type="button" onClick={clearRecents} style={{ fontSize: 11, color: "var(--text-muted)", background: "transparent", border: "none", cursor: "pointer", padding: "2px 4px" }}>Clear</button>
                    </div>
                    {recent.map((term, idx) => (
                      <button
                        key={term}
                        type="button"
                        onMouseEnter={() => setActive(idx)}
                        onClick={() => applyRecent(term)}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "8px 12px", borderRadius: 9, border: "1px solid transparent", background: idx === active ? "rgba(241,48,9,0.12)" : "transparent", cursor: "pointer" }}
                      >
                        <Clock size={14} style={{ opacity: 0.55, flexShrink: 0 }} />
                        <span style={{ minWidth: 0, flex: 1, fontSize: 13.5, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{term}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 18, fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>Type at least 2 characters to search internships, employers, roles, or skills.</div>
                )
              ) : items.length === 0 && !loading ? (
                <div style={{ padding: 18, fontSize: 13, color: "var(--text-muted)", textAlign: "center" }}>No matches for &quot;{q.trim()}&quot;.</div>
              ) : (
                // One relevance-ranked list, all kinds interleaved. A per-row tag keeps the kind
                // visible without imposing a fixed order.
                items.map((it, idx) => (
                  <button
                    key={it.key}
                    type="button"
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => run(it)}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "8px 12px", borderRadius: 9, border: "1px solid transparent", background: idx === active ? "rgba(241,48,9,0.12)" : "transparent", cursor: "pointer" }}
                  >
                    <span style={{ minWidth: 0, flex: 1 }}>
                      <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</span>
                      {it.subtitle && <span style={{ display: "block", fontSize: 12, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.subtitle}</span>}
                    </span>
                    <span style={{ flexShrink: 0, fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--text-muted)", fontFamily: "var(--gtm-mono, ui-monospace)", border: "1px solid var(--border-subtle)", borderRadius: 999, padding: "2px 7px" }}>{it.kind}</span>
                  </button>
                ))
              )}
            </div>

            <div style={{ display: "flex", gap: 12, alignItems: "center", padding: "8px 14px", borderTop: "1px solid var(--border-subtle)", fontSize: 11, color: "var(--text-muted)" }}>
              <span><kbd style={KBD}>↑↓</kbd> navigate</span>
              <span><kbd style={KBD}>↵</kbd> open</span>
              <span><kbd style={KBD}>esc</kbd> close</span>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
